import { isRateLimitError } from '@homerunner-next/widget-core/runtime'

/**
 * Shared visible error state for a widget whose data fetch failed (#740).
 * Previously most widgets returned `null` on a data-fetch error, so a network
 * blip or a 429 rate limit rendered as a blank space (or an endless skeleton) —
 * indistinguishable from "no content". This surfaces the failure instead:
 * a 429 reads as temporary/retry (amber), anything else as a load failure
 * (neutral gray).
 *
 * Kept dependency-free with inline styles so it renders identically inside every
 * widget's shadow root without pulling in per-widget CSS.
 */
export default function WidgetDataError({ error }: { error?: unknown }) {
  const rateLimited = isRateLimitError(error)
  return (
    <div
      // widget-core's vocabulary, not the legacy `data-fetch-failed` (#907):
      // value-sensitive consumers (extension x-ray, board tooling) must match
      // every fetch failure with one grep, and widget-core owns the attribute
      // lifecycle (client-wrapper.tsx).
      data-hr-error={rateLimited ? 'rate-limited' : 'widget-fetch-failed'}
      role="alert"
      style={{
        // Neutral for a generic failure (a customer can ship with it on a live
        // page — not an alarming red box), amber for a temporary 429 (#741).
        padding: '10px 14px',
        font: '13px/1.45 system-ui, -apple-system, sans-serif',
        color: rateLimited ? '#92400e' : '#4b5563',
        background: rateLimited ? '#fffbeb' : '#f9fafb',
        border: `1px solid ${rateLimited ? '#fde68a' : '#e5e7eb'}`,
        borderRadius: 8,
      }}
    >
      {rateLimited
        ? 'This page is loading a lot of content right now and hit a temporary rate limit. Please wait a few seconds and refresh.'
        : 'Couldn’t load this content. Please check your connection and refresh the page.'}
    </div>
  )
}
